// Dashboard Page

import { Link } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { useAuthStore } from '../../stores/auth-store';

function getTrialDaysLeft(trialEndsAt: string): number {
  const trialEnd = new Date(trialEndsAt);
  const now = new Date();
  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / millisecondsPerDay);

  return Math.max(0, daysLeft);
}

export function DashboardPage() {
  const { user } = useAuthStore();

  const { data: tenant, isLoading: tenantLoading } = trpc.tenant.getCurrent.useQuery();
  const { data: usage, isLoading: usageLoading } = trpc.tenant.getUsage.useQuery();

  // Determine if subscription needs attention
  const subscriptionStatus = tenant?.subscription?.status;
  const needsSubscription = !tenant?.subscription || !['active', 'trialing', 'past_due'].includes(subscriptionStatus ?? '');
  const isPastDue = subscriptionStatus === 'past_due';
  const isCanceled = subscriptionStatus === 'canceled';

  return (
    <div className="space-y-6">
      {/* Subscription alert banner */}
      {tenant && needsSubscription && (
        <div className={`rounded-lg p-4 ${isCanceled ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center gap-3">
            <svg className={`w-5 h-5 ${isCanceled ? 'text-red-600' : 'text-amber-600'}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className={`font-medium ${isCanceled ? 'text-red-800' : 'text-amber-800'}`}>
                {isCanceled ? 'Your subscription has been canceled' : 'Subscription required'}
              </p>
              <p className={`text-sm ${isCanceled ? 'text-red-700' : 'text-amber-700'}`}>
                {isCanceled
                  ? 'Reactivate your subscription to regain access to all features.'
                  : 'Subscribe to a plan to access Calculom features.'}
              </p>
            </div>
            <Link
              to="/settings/billing"
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                isCanceled
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {isCanceled ? 'Reactivate' : 'Subscribe'}
            </Link>
          </div>
        </div>
      )}

      {/* Past due warning */}
      {tenant && isPastDue && (
        <div className="rounded-lg p-4 bg-yellow-50 border border-yellow-200">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="font-medium text-yellow-800">Payment past due</p>
              <p className="text-sm text-yellow-700">Please update your payment method to avoid service interruption.</p>
            </div>
            <Link
              to="/settings/billing"
              className="px-4 py-2 rounded-lg font-medium text-sm bg-yellow-600 text-white hover:bg-yellow-700"
            >
              Update Payment
            </Link>
          </div>
        </div>
      )}

      {/* Welcome section */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.displayName ?? 'User'}
        </h1>
        <p className="text-gray-600">Here's what's happening in your organization.</p>
      </div>

      {/* Subscription status */}
      {tenant && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-medium text-gray-900">Subscription</h2>
              <p className="text-sm text-gray-600">
                {tenant.subscription?.plan?.name ?? 'No active subscription'}
              </p>
            </div>
            <div className="text-right">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  subscriptionStatus === 'active'
                    ? 'bg-green-100 text-green-800'
                    : subscriptionStatus === 'trialing'
                    ? 'bg-blue-100 text-blue-800'
                    : subscriptionStatus === 'past_due'
                    ? 'bg-yellow-100 text-yellow-800'
                    : subscriptionStatus === 'canceled'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {subscriptionStatus === 'trialing' ? 'trial' : subscriptionStatus ?? 'inactive'}
              </span>
              {tenant.subscription?.trialEndsAt && subscriptionStatus === 'trialing' && (
                <p className="mt-1 text-xs text-gray-500">
                  {getTrialDaysLeft(tenant.subscription.trialEndsAt)} days left in trial, ends {new Date(tenant.subscription.trialEndsAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {/* Products */}
          {tenant.subscription?.products && tenant.subscription.products.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Active Products</h3>
              <div className="flex flex-wrap gap-2">
                {tenant.subscription.products.map((product: { id: string; name: string; tier: string }) => (
                  <span
                    key={product.id}
                    className="inline-flex items-center rounded-lg bg-primary-50 px-3 py-1 text-sm font-medium text-primary-700"
                  >
                    {product.name}
                    <span className="ml-2 text-xs text-primary-500">({product.tier})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Usage stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <UsageCard
          title="Users"
          current={usage?.users.current ?? 0}
          limit={usage?.users.limit ?? 0}
          loading={usageLoading}
        />
        <UsageCard
          title="ERP Connections"
          current={usage?.connections.current ?? 0}
          limit={usage?.connections.limit ?? 0}
          loading={usageLoading}
        />
        <UsageCard
          title="Companies"
          current={usage?.companies.current ?? 0}
          limit={usage?.companies.limit ?? 0}
          loading={usageLoading}
        />
        <UsageCard
          title="Templates"
          current={usage?.templates.current ?? 0}
          limit={usage?.templates.limit ?? 0}
          loading={usageLoading}
        />
      </div>

      {/* Quick actions */}
      <div className="card">
        <h2 className="font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <QuickAction
            title="Invite Users"
            description="Add team members to your organization"
            href="/settings/users"
            icon="/assets/icons/icon-team.png"
          />
          <QuickAction
            title="Connect ERP"
            description="Set up your ERP integration"
            href="/erp"
            icon="/assets/icons/icon-connection.png"
          />
          <QuickAction
            title="View Reports"
            description="Access your financial reports"
            href="/reports"
            icon="/assets/icons/icon-document.png"
          />
          <QuickAction
            title="Install Add-in"
            description="Get the Calculom Excel add-in"
            href="/install-addin"
            icon="/assets/icons/icon-connection.png"
          />
        </div>
      </div>
    </div>
  );
}

function UsageCard({
  title,
  current,
  limit,
  loading,
}: {
  title: string;
  current: number;
  limit: number;
  loading: boolean;
}) {
  const percentage = limit === -1 ? 0 : limit > 0 ? (current / limit) * 100 : 0;
  const isUnlimited = limit === -1;

  return (
    <div className="card">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      {loading ? (
        <div className="mt-2 h-8 w-20 animate-pulse rounded bg-gray-200" />
      ) : (
        <>
          <p className="mt-2 text-2xl font-semibold text-gray-900">
            {current}
            {!isUnlimited && (
              <span className="text-sm font-normal text-gray-500">/{limit}</span>
            )}
            {isUnlimited && <span className="text-sm font-normal text-gray-500">/∞</span>}
          </p>
          {!isUnlimited && (
            <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
              <div
                className={`h-1.5 rounded-full ${
                  percentage > 90 ? 'bg-red-500' : percentage > 70 ? 'bg-yellow-500' : 'bg-primary-500'
                }`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function QuickAction({
  title,
  description,
  href,
  icon,
}: {
  title: string;
  description: string;
  href: string;
  icon: string;
}) {
  return (
    <Link
      to={href}
      className="flex items-start gap-4 rounded-lg border border-gray-200 p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors"
    >
      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
        <img src={icon} alt="" className="w-6 h-6" />
      </div>
      <div>
        <h3 className="font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </Link>
  );
}
